import { useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useScrollReveal } from "../components/useScrollReveal";
import { useSEO } from "../components/useSEO";
import "./About.css";

/**
 * Contact form submits straight to Web3Forms (https://web3forms.com) —
 * a client-side-only form delivery service, so this works no matter
 * where the static site itself is hosted (Render today, Azure later,
 * or anywhere else) without needing a backend of our own. The access
 * key below is a PUBLIC key by design (Web3Forms' own docs: "You can
 * use it in client side code"), so it's safe to ship in the bundle.
 * Submissions land as an email at the address the key is registered
 * to. The honeypot field is an extra spam-filter layer on top of
 * Web3Forms' own bot protection.
 */
const WEB3FORMS_ACCESS_KEY = "e0591985-4024-4795-88df-2d599bcb7426";
export default function About() {
  useScrollReveal();
  useSEO(
    "About & Leadership | Modus",
    "Meet the clinicians and operators behind Modus, the team measuring physician quality on real patient outcomes instead of billing codes."
  );
  const [honeypot, setHoneypot] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [openCards, setOpenCards] = useState(new Set());

  const toggleCard = (idx) => {
    setOpenCards((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("");

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    // Honeypot: real visitors never fill this in. If it's filled, silently drop.
    if (honeypot) return;

    const form = e.target;
    setIsSending(true);
    setStatusMessage("");
    setStatusType("");

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "New Book a Demo request from modus.org",
          from_name: "Modus Website",
          name: form.name.value,
          email: form.email.value,
          company: form.company.value,
          message: form.message.value,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Request failed with status " + res.status);
      form.reset();
      setStatusMessage("Thanks, we'll be in touch shortly.");
      setStatusType("success");
    } catch (err) {
      setStatusMessage("Something went wrong sending that. Please email hello@modus.org directly.");
      setStatusType("error");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <SiteHeader active="about" />


<section className="about-hero">
  <div className="wrap about-hero-grid">
    <div className="about-hero-copy">
      <p className="kicker">About Modus</p>
      <h1>Better care costs less because it's measured, thoughtful, and well-planned to avoid harmful and expensive complications.</h1>
      <p>We're dedicated to our customers' success, with deep knowledge across medicine, digital technology, operational excellence, and executive leadership.</p>
    </div>
    <div className="about-hero-visual">
      <img src="/patented-badge.png" alt="Patented, protected" className="patented-badge-about" />
    </div>
  </div>
</section>

<section>
  <div className="wrap">
    <div className="section-head reveal center">
      <p className="kicker">Watch</p>
      <h2 className="section-title">Hear it directly from our founder.</h2>
    </div>
    <div className="founder-video reveal">
      <figure style={{margin: 0}}>
        <video controls playsInline preload="metadata" poster="/modus-founder-poster.jpg">
          <source src="/modus-founder-story.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <figcaption>Ali Alhimiri, MD</figcaption>
      </figure>
      <div className="founder-quote">
        <blockquote>"There is nothing wrong with profits if we provide value in return, but we should never promote harm to achieve profit."</blockquote>
        <div className="founder-name">Ali Alhimiri, M.D.</div>
        <div className="founder-role">Founder and CEO of Modus</div>
        <Link className="founder-link" to="/blog/harmed-by-the-healer-dangers-of-mindless-aggressive-care">Read Ali's Story →</Link>
      </div>
    </div>
  </div>
</section>

<section>
  <div className="wrap">
    <div className="section-head reveal">
      <p className="kicker">The team</p>
      <h2 className="section-title">The people behind the score.</h2>
      <p className="section-lead">Click a card to read the full bio.</p>
    </div>
    <div className="team-grid reveal-stagger-3d" id="teamGrid">
      <div className={`team-card ${openCards.has(0) ? "open" : ""}`} onClick={() => toggleCard(0)}>
        <img className="team-photo" src="/Ali-Alhimiri-300.jpg" alt="Ali Alhimiri"/>
        <div className="team-info">
          <div className="team-name">Ali Alhimiri, MD</div>
          <div className="team-role">Founder &amp; CEO</div>
          <div className="team-bio">Physical Medicine &amp; Rehabilitation physician providing cost-effective, patient-function-centered care since 2002. His insight directly led to Modus's approach. MD from University of Baghdad; residency at Wayne State University-affiliated hospitals, Detroit; board certified by the American Academy of PM&amp;R, 2003.</div>
          <span className="team-toggle">{openCards.has(0) ? "Hide bio" : "Read bio"} <span className={`team-toggle-arrow ${openCards.has(0) ? "is-open" : ""}`}>&#8595;</span></span>
        </div>
      </div>
      <div className={`team-card ${openCards.has(1) ? "open" : ""}`} onClick={() => toggleCard(1)}>
        <img className="team-photo" src="/Bennett300.jpg" alt="Steve Bennett"/>
        <div className="team-info">
          <div className="team-name">Steve Bennett</div>
          <div className="team-role">Co-Founder &amp; COO</div>
          <div className="team-bio">Background in Engineering and Change Management, driving improvement across multiple global business sectors. Shaped how Modus operates and interfaces within client processes and technology.</div>
          <span className="team-toggle">{openCards.has(1) ? "Hide bio" : "Read bio"} <span className={`team-toggle-arrow ${openCards.has(1) ? "is-open" : ""}`}>&#8595;</span></span>
        </div>
      </div>
      <div className={`team-card ${openCards.has(2) ? "open" : ""}`} onClick={() => toggleCard(2)}>
        <img className="team-photo" src="/Kamalapurkar300.jpg" alt="Manohar Kamalapurkar"/>
        <div className="team-info">
          <div className="team-name">Manohar Kamalapurkar, MBA</div>
          <div className="team-role">Product Director</div>
          <div className="team-bio">Extensive experience in complex, large-scale enterprise Business Analysis &amp; Product Design. Accelerates digital transformation in Healthcare IT through Cloud, Business Intelligence, Interoperability, and Machine Learning. A strong believer in open data and APIs to overcome data silos.</div>
          <span className="team-toggle">{openCards.has(2) ? "Hide bio" : "Read bio"} <span className={`team-toggle-arrow ${openCards.has(2) ? "is-open" : ""}`}>&#8595;</span></span>
        </div>
      </div>
      <div className={`team-card ${openCards.has(3) ? "open" : ""}`} onClick={() => toggleCard(3)}>
        <img className="team-photo" src="/Derrick-Amato300.jpg" alt="Derrick Amato"/>
        <div className="team-info">
          <div className="team-name">Derrick Amato</div>
          <div className="team-role">Advisory Board</div>
          <div className="team-bio">Seasoned executive across workers' comp, insurance, and managed care. Former VP of National Sales at Ascellus Behavioral Health; CEO/President of Connecticut Occupational Medicine Partners; President of Concentra's Clinical Division. Served on the CT Workers' Compensation Medical Advisory Panel.</div>
          <span className="team-toggle">{openCards.has(3) ? "Hide bio" : "Read bio"} <span className={`team-toggle-arrow ${openCards.has(3) ? "is-open" : ""}`}>&#8595;</span></span>
        </div>
      </div>
    </div>
  </div>
</section>

<section id="contact">
  <div className="wrap">
    <div className="contact-band reveal">
      <div>
        <h2>Get in touch</h2>
        <p>Send us a message, or book time directly with our team to see the platform on your own network's data.</p>
      </div>
      <form className="contact-form" onSubmit={handleContactSubmit}>
        <input type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} className="hp-field" tabIndex="-1" autoComplete="off" aria-hidden="true" />
        <input type="text" name="name" placeholder="Full name" aria-label="Full name" required />
        <input type="email" name="email" placeholder="Work email" aria-label="Work email" required />
        <input type="text" name="company" placeholder="Company" aria-label="Company" />
        <textarea name="message" placeholder="How can we help?" aria-label="How can we help?" required></textarea>
        <button className="btn btn-teal btn-lg" type="submit" disabled={isSending}>
          {isSending ? "Sending…" : "Send message"}
        </button>
        <p className={`form-status ${statusType}`} role="status" aria-live="polite">
          {statusMessage}
        </p>
      </form>
    </div>
  </div>
</section>


      <SiteFooter />
    </>
  );
}
